import React, { useEffect, useRef, useState } from "react";

import Template from "../smart-cale/Template";
import {
  parseMessageFromNative,
  sendMessageToParent,
} from "src/helpers/common";
import { POST_MESSAGE_EVENTS } from "src/constants/common";

export type BluetoothStatus = "unknown" | "on" | "off" | "denied";

interface BluetoothCardProps {
  /** Notify parent when status updates (e.g. for footer button label) */
  onStatusChange?: (status: BluetoothStatus) => void;
}

const STICKY_ON_MS = 1200;

const BluetoothCard: React.FC<BluetoothCardProps> = ({ onStatusChange }) => {
  const [bluetoothStatus, setBluetoothStatus] =
    useState<BluetoothStatus>("unknown");
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  const statusRef = useRef<BluetoothStatus>("unknown");
  const pendingOffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyStatus = (newStatus: BluetoothStatus) => {
    if (statusRef.current === newStatus) return;
    statusRef.current = newStatus;
    setBluetoothStatus(newStatus);
    onStatusChangeRef.current?.(newStatus);
  };

  const updateStatus = (newStatus: BluetoothStatus) => {
    if (newStatus === "on") {
      if (pendingOffTimerRef.current) {
        clearTimeout(pendingOffTimerRef.current);
        pendingOffTimerRef.current = null;
      }
      applyStatus("on");
      return;
    }
    if (newStatus === "off" && statusRef.current === "on") {
      if (pendingOffTimerRef.current) return;
      pendingOffTimerRef.current = setTimeout(() => {
        pendingOffTimerRef.current = null;
        applyStatus("off");
      }, STICKY_ON_MS);
      return;
    }
    if (pendingOffTimerRef.current) {
      clearTimeout(pendingOffTimerRef.current);
      pendingOffTimerRef.current = null;
    }
    applyStatus(newStatus);
  };

  // Poll RN for status every 1s so we get updates even if native listener misses
  useEffect(() => {
    sendMessageToParent(POST_MESSAGE_EVENTS.CHECK_BLUETOOTH_STATUS, {});
    const timer = setInterval(() => {
      sendMessageToParent(POST_MESSAGE_EVENTS.CHECK_BLUETOOTH_STATUS, {});
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    return () => {
      if (pendingOffTimerRef.current) clearTimeout(pendingOffTimerRef.current);
    };
  }, []);

  // Single listener for all Bluetooth events from RN; only update when status actually changed
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const payload = parseMessageFromNative(event.data);
      if (!payload) return;
      const action = (payload.action || payload.type) as string;

      if (action === POST_MESSAGE_EVENTS.BLUETOOTH_ON) {
        updateStatus("on");
        return;
      }
      if (action === POST_MESSAGE_EVENTS.BLUETOOTH_OFF) {
        updateStatus("off");
        return;
      }
      if (action === POST_MESSAGE_EVENTS.BLUETOOTH_PERMISSION_DENIED) {
        updateStatus("denied");
        return;
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <Template
      title="GoodFlip Care Would Like to Use Bluetooth"
      titleClassName="text-[1.75rem] font-semibold text-center mt-4 text-gray-900"
      description={
        <div className="space-y-4">
          <p className="text-center text-[var(--text-secondary)]">
            Bluetooth is required to pair your Smart Scale.
          </p>
          {bluetoothStatus !== "unknown" && (
            <p className="text-center text-sm">
              Status:{" "}
              <span
                className={
                  bluetoothStatus === "on"
                    ? "text-green-600"
                    : bluetoothStatus === "denied"
                    ? "text-red-600"
                    : "text-[var(--text-muted)]"
                }
              >
                {bluetoothStatus === "on"
                  ? "Bluetooth is on"
                  : bluetoothStatus === "denied"
                  ? "Bluetooth access denied"
                  : "Bluetooth is off"}
              </span>
            </p>
          )}
        </div>
      }
    />
  );
};

export default BluetoothCard;
